/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Layout,
  RankedTester,
  rankWith,
  RendererProps,
  uiTypeIs,
} from "@jsonforms/core";
import {
  JsonFormsDispatch,
  useJsonForms,
  withJsonFormsLayoutProps,
} from "@jsonforms/react";
import {
  getHeaders,
  getEndpointName,
} from "@apollo/jsonforms-react-spectrum-renderers/src/util/commonUtils";
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import _, { get } from "lodash";
import Loader from "../components/Loader/Loader";

// Utils
import { evaluateContextRule, extractPath } from "../utils/commonUtils";
import { setReduxState } from "../redux/actions/SetReduxStateAction";

export const dynamicSectionRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("DynamicSection")
);

function DynamicSectionRenderer({
  uischema,
  schema,
  path,
  visible,
}: RendererProps) {
  const { renderers, cells, config } = useJsonForms();
  const [loading, setLoading] = useState(true);
  const [headersLoaded, setHeadersLoaded] = useState(false);
  const dynamicSection = uischema as Layout;
  let dataUrl = uischema.options?.dataUrl;
  const dataUrlKeys = uischema.options?.dataUrlKeys;
  const fullScreenLoader = uischema.options?.fullScreenLoader ?? true;

  const name = uischema.options?.name;
  const endpointPath = uischema.options?.endpointPath;
  /* option to provide any additional data to header  */
  const additionalHeaders = uischema.options?.additionalHeaders;
  const authToken = uischema.options?.authToken || false;

  const dispatch = useDispatch();
  const dynamicFailureCallback =
    config?.lifecycleHooks?.onDynamicSectionFailure;

  const preprocess = config?.lifecycleHooks?.dynamicSection?.preprocess;

  // after submitting the form and success response if user wants to reload the dynmaic section
  const dynamicSectionReload = config?.dynamicSection;
  const reloadSection =
    dynamicSectionReload?.reload === undefined
      ? true
      : dynamicSectionReload?.reload;

  /** merges headers with additional headers if defined  */
  const apiHeaders = additionalHeaders
    ? _.unionBy(additionalHeaders, config?.apiHeaders, "name")
    : config?.apiHeaders;
  const headers = getHeaders(apiHeaders);
  // data to be used for dynamic keys present in dataUrl
  // eg: data = {
  //      workspace: 'workspace-id-here',
  //      id: 'id-here'
  //   }
  const data = config?.dynamicData?.data || {};

  // if dataUrl has dynamic keys then append here
  // eg: 'v1/${workspace}/connector/${id}' => 'v1/workspace-id-here/connector/id-here'
  if (dataUrlKeys?.length && data) {
    dataUrlKeys.forEach((item: any) => {
      dataUrl =
        item && data[item]
          ? dataUrl.replace(`\${${item}}`, data[item])
          : dataUrl;
    });
  }

  // if API need Authorization Token But session storage is loading after page Load.
  if (authToken) {
    if (headers.get("Authorization") && !headersLoaded) {
      setHeadersLoaded(true);
    }
  } else if (!headersLoaded) {
    setHeadersLoaded(true);
  }

  if (endpointPath) {
    const pathKey = getEndpointName(endpointPath, config);
    const endpoint = get(config, pathKey, "");
    dataUrl = endpoint + dataUrl;
  }

  headers.append("content-type", "application/json;charset=UTF-8");

  useEffect(() => {
    if (dataUrl && headersLoaded && reloadSection) {
      const fetchParams = {
        method: "GET",
        headers,
      };
      setLoading(true);
      fetch(dataUrl, fetchParams)
        .then(async (response) => {
          if (response.ok) {
            const resData = await response.json();
            if (name) {
              // Clone the state to avoid mutating the state directly
              const processedData = preprocess
                ? preprocess(name, resData)
                : resData;
              dispatch(
                setReduxState({
                  [name]: processedData,
                })
              );
            }
          } else if (!response.ok) {
            if (dynamicFailureCallback) {
              dynamicFailureCallback(response);
            }
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dataUrl, headersLoaded, reloadSection]); // console.log({ dataUrl, loader: isLoading || !pageContext[name], isLoading, context: JSON.stringify(pageContext[name]), })

  // Determine if the component should be visible
  let componentVisibility = visible;

  // If there is a context rule, evaluate it and use that visibility rule instead.
  if (uischema.options?.contextRule) {
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    const reduxStateData = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    componentVisibility = evaluateContextRule(
      uischema.options?.contextRule,
      reduxStateData
    )?.visible;
  }

  return (
    <div hidden={!componentVisibility}>
      {/* Wait till API is loaded and context is updated */}
      {loading ? (
        <Loader fullScreen={fullScreenLoader} />
      ) : (
        <JsonFormsDispatch
          renderers={renderers}
          cells={cells}
          uischema={dynamicSection.elements[0]}
          schema={schema}
          path={path}
        />
      )}
    </div>
  );
}

export default withJsonFormsLayoutProps(DynamicSectionRenderer);
